import { z } from 'zod'

import {
  accept,
  isPlayersTurn,
  reject,
  type ActionContext,
  type GameEngine,
  type PlaySummary,
  type Player,
  type TurnState,
} from '../engine.js'
import type { Rng } from '../rng.js'
import type { TeddyKey } from '../teddies.js'
import { type SkillLevels } from './maths-skills.js'
import { mosaicSkillOf } from './mosaic-skills.js'
import { cluesOf, picturesOfSize, pictureByKey, squaresOf } from './mosaic-pictures.js'
import { isPictureSolved, MOSAIC_SIZES, type Clues, type MosaicSize } from './mosaic-solve.js'

/**
 * Maritza's Mosaic.
 *
 * A nonogram: numbers along the top and down the side say how long the runs of
 * filled squares are, and what falls out of working them all out is a picture.
 *
 * The picture is the whole reason this exists rather than being a second
 * sudoku. Every other game in the roster tells her how a round went in the
 * round's own terms — right answers, lives left, cards turned. This one hands
 * her something she made by reasoning, which is a different and better kind of
 * feedback than a count.
 */

const LARGEST_GRID = MOSAIC_SIZES.reduce((most, size) => (size > most ? size : most))

export const DEFAULT_MOSAIC_SIZE: MosaicSize = 5

/**
 * The three states a square can be in.
 *
 * `marked` is the one a nonogram cannot be played without: it is how she notes
 * "I have worked out this one is empty", and it is the entire method — you
 * find the blanks and the filled squares fall out. Leaving it to a two-state
 * grid would make the game far harder than it should be for nothing gained.
 *
 * It is also **not part of the answer.** A mark is her working; the picture is
 * the filled squares. So a finished picture with every mark in the wrong place
 * is still finished, and a grid marked perfectly with nothing filled in is not
 * finished at all. `mosaic-solve.ts` is handed filled squares alone and could
 * not consult a mark if it wanted to.
 */
export const CELL_STATES = ['unknown', 'filled', 'marked'] as const

export type CellState = (typeof CELL_STATES)[number]

export const mosaicAction = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('set'),
    cell: z
      .number()
      .int()
      .min(0)
      .max(LARGEST_GRID * LARGEST_GRID - 1),
    /**
     * What the square becomes, rather than "cycle it".
     *
     * The screen cycles on one key, which is what she presses; the action says
     * where that landed. A `cycle` action would mean the server's answer
     * depended on a state the client believed it was in, so one dropped
     * message would put the two grids one step out of phase for the rest of
     * the round — and the replay would score a picture she never drew.
     */
    state: z.enum(CELL_STATES),
  }),
  /**
   * Rub the whole thing out and start again.
   *
   * An action, for the reason Stripey's is: the server scores a round by
   * replaying the moves it was told about, so a reset it never heard of would
   * leave its grid holding squares hers no longer has.
   */
  z.object({ type: z.literal('clear') }),
])

export type MosaicAction = z.infer<typeof mosaicAction>

export interface MosaicConfig {
  readonly size?: MosaicSize
  /** A particular picture, where a round is being replayed rather than dealt. */
  readonly pictureKey?: string
  readonly levels?: SkillLevels
  readonly startedAt?: number
}

export interface MosaicState {
  readonly pictureKey: string
  readonly size: MosaicSize
  /** Her grid: what she has filled in and what she has marked. */
  readonly squares: readonly CellState[]
  readonly levels: SkillLevels
  readonly status: 'playing' | 'finished'
  readonly turn: TurnState
  readonly startedAt: number
  /**
   * How many times she has changed her mind about a square she had settled.
   *
   * Counted rather than recorded one by one, exactly as Stripey's counts
   * rubbing out: it measures how much the picture was a fight, and no parent
   * wants two hundred rows of somebody thinking. Cycling an untouched square
   * to filled is not a change of mind — it is the first thing she said about
   * it.
   */
  readonly changes: number
}

/** What she is told when the picture is finished, and not one moment before. */
export interface MosaicReveal {
  readonly key: string
  readonly title: string
  readonly teddy: TeddyKey | null
}

export interface MosaicView {
  readonly size: MosaicSize
  /** The numbers she works from, which are the whole of the puzzle. */
  readonly clues: Clues
  readonly squares: readonly CellState[]
  readonly status: MosaicState['status']
  readonly currentPlayerId: string | null
  /** Squares she has filled in, of the squares the picture has. */
  readonly filledIn: number
  readonly toFill: number
  readonly changes: number
  /**
   * Who it turned out to be — **null until it is finished.**
   *
   * The title and the teddy are the answer. Sending them while she is still
   * working would put "Turtley" on the screen above a half-drawn Turtley, which
   * is the one thing this game has to give and the one thing it must not give
   * away. The same rule as Stripey's cell view, which never carries the
   * solution's value for an empty square.
   */
  readonly reveal: MosaicReveal | null
}

/** Which squares she has filled, which is the only part that is her answer. */
function filledSquares(state: MosaicState): boolean[] {
  return state.squares.map((square) => square === 'filled')
}

export function cluesForState(state: MosaicState): Clues {
  return cluesOf(pictureByKey(state.pictureKey))
}

export function toMosaicView(state: MosaicState): MosaicView {
  const picture = pictureByKey(state.pictureKey)
  const answer = squaresOf(picture)

  return {
    size: state.size,
    clues: cluesOf(picture),
    squares: state.squares,
    status: state.status,
    currentPlayerId: state.turn.currentPlayerId,
    filledIn: state.squares.filter((square) => square === 'filled').length,
    toFill: answer.filter(Boolean).length,
    changes: state.changes,
    reveal:
      state.status === 'finished'
        ? { key: picture.key, title: picture.title, teddy: picture.teddy }
        : null,
  }
}

/**
 * The round, as a parent will read it months later.
 *
 * One of these per picture, not one per square — the reasoning is Stripey's,
 * and it applies harder here: a 15×15 has two hundred and twenty-five squares,
 * and a child who marks a square empty, works out she was wrong and fills it
 * in has done the puzzle exactly right. Recording that as a wrong answer
 * followed by a right one describes good reasoning as flailing.
 */
export interface MosaicAttempt {
  readonly skillKey: string
  readonly prompt: string
  readonly expected: number
  readonly given: number
  readonly correct: boolean
  readonly elapsedMs: number
  readonly playerId: string
}

export function mosaicAttempt(
  state: MosaicState,
  playerId: string,
  endedAt: number,
): MosaicAttempt {
  const picture = pictureByKey(state.pictureKey)
  const toFill = squaresOf(picture).filter(Boolean).length
  const filledIn = state.squares.filter((square) => square === 'filled').length

  return {
    skillKey: mosaicSkillOf(state.size).key,
    prompt: [
      `A ${state.size}×${state.size} picture of ${picture.title.replace(/^A /, 'a ')}`,
      state.status === 'finished'
        ? `finished with ${state.changes} changes of mind`
        : `left with ${filledIn} of ${toFill} squares filled in`,
    ].join(', '),
    // The picture was either finished or it was not, so what was "expected" is
    // every square it has and what was "given" is how many she filled.
    expected: toFill,
    given: filledIn,
    correct: state.status === 'finished',
    elapsedMs: Math.max(0, endedAt - state.startedAt),
    playerId,
  }
}

/** The sizes she has a level in, which is every size the game deals. */
export function mosaicLevelsTouched(state: MosaicState): SkillLevels {
  return { [mosaicSkillOf(state.size).key]: state.levels[mosaicSkillOf(state.size).key] ?? 0 }
}

export const mosaicEngine: GameEngine<MosaicConfig, MosaicState, MosaicAction> = {
  key: 'mosaic',
  actionSchema: mosaicAction,

  createInitialState(config: MosaicConfig, players: readonly Player[], rng: Rng): MosaicState {
    const size = config.size ?? DEFAULT_MOSAIC_SIZE
    /*
     * The level is recorded but does not yet choose.
     *
     * Stripey's shapes a puzzle from where she stands, because it generates
     * one. A mosaic's pictures are drawn by hand and there is one band of them
     * per size, so at present the size *is* the difficulty. Said plainly here
     * rather than left as a silent unused argument: when the set grows enough
     * to have easy and hard pictures at one size, this is where that goes.
     */
    const levels = config.levels ?? {}
    const picture = config.pictureKey
      ? pictureByKey(config.pictureKey)
      : rng.pick(picturesOfSize(size))

    return {
      pictureKey: picture.key,
      size: picture.size,
      squares: new Array<CellState>(picture.size * picture.size).fill('unknown'),
      levels,
      status: 'playing',
      turn: { players, currentPlayerId: players[0]?.id ?? null },
      startedAt: config.startedAt ?? 0,
      changes: 0,
    }
  },

  reduce(state, action, context: ActionContext) {
    if (state.status !== 'playing') return reject(state, 'game-over')
    if (!isPlayersTurn(state.turn, context.playerId)) return reject(state, 'not-your-turn')

    if (action.type === 'clear') {
      // Nothing of hers on the grid, so nothing to rub out. Refused rather
      // than accepted as a no-op, so the move log does not fill with resets
      // from somebody leaning on the key.
      if (state.squares.every((square) => square === 'unknown')) {
        return reject(state, 'already-played')
      }

      return accept({
        ...state,
        squares: new Array<CellState>(state.size * state.size).fill('unknown'),
        changes: state.changes + 1,
      })
    }

    if (action.cell >= state.size * state.size) return reject(state, 'invalid-action')

    const was = state.squares[action.cell] ?? 'unknown'
    // A repeated keypress on a square she has already put in that state is a
    // keyboard repeating, not a decision.
    if (was === action.state) return reject(state, 'already-played')

    const squares = [...state.squares]
    squares[action.cell] = action.state

    const finished = isPictureSolved(
      squares.map((square) => square === 'filled'),
      cluesForState(state),
    )

    return accept({
      ...state,
      squares,
      // Saying something about an untouched square is not changing her mind.
      changes: was === 'unknown' ? state.changes : state.changes + 1,
      status: finished ? 'finished' : 'playing',
      turn: { ...state.turn, currentPlayerId: finished ? null : state.turn.currentPlayerId },
    })
  },

  isTerminal(state) {
    return state.status === 'finished'
  },

  summarise(state, endedAt): PlaySummary {
    return {
      gameKey: 'mosaic',
      result: state.status === 'finished' ? 'won' : 'abandoned',
      durationMs: Math.max(0, endedAt - state.startedAt),
      difficulty: `${state.size}×${state.size}`,
    }
  },
}

export { filledSquares }
